import dayjs from 'dayjs';
import { useTheme } from 'styled-components';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { EventClickArg } from '@fullcalendar/core';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { ButtonColors } from 'types';
import { getPlural } from 'utils/helpers';
import { isMobile, useElementSize } from 'hooks';

import { Header } from './header';
import * as Styles from './styles';
import { eventContent, allDayContent, dayHeaderContent } from './config';
import {
  EventInput,
  EventSourceInput,
  EventCalendarPage,
  EventCalendarProps,
} from './types';

export const EventCalendar = ({
  height,
  events,
  header,
  datesSet,
  eventsSet,
  eventClick,
  calendarRef,
  headerRightSlot,
  removeEventScroll,
  enableAllDayClick,
  showSummary = true,
  ...props
}: EventCalendarProps) => {
  const mobile = isMobile();
  const ref = useRef<FullCalendar | null>(null);
  const [isEventsLoaded, setEventsLoaded] = useState(false);
  const [visible, setVisibleDates] = useState<EventCalendarPage>();
  const [headerRef, { height: headerHeight }] = useElementSize();
  const isMonthView = ref?.current?.getApi()?.view?.type === 'dayGridMonth';
  const theme = useTheme();

  useEffect(() => {
    if (calendarRef) {
      calendarRef.current = ref.current;
    }
  }, [calendarRef, ref.current]);

  const calendarHeight = useMemo(() => {
    if (!headerHeight) {
      return undefined;
    }
    return `calc(${height} - ${headerHeight}px)`;
  }, [height, headerHeight]);

  const eventsWrap = useMemo(() => {
    if (!events || !Array.isArray(events) || isMonthView) {
      return events;
    }
    if (!events.length) {
      return events;
    }

    const eventsData: EventInput[] = [...events];

    if (showSummary && visible) {
      const format = 'YYYY-MM-DD';
      const startView = dayjs(visible.start);
      const endView = dayjs(visible.end).subtract(1, 'day');

      const ranges = events.map((event) => ({
        start: dayjs(event.start as Date).format(format),
        end: dayjs(event.end as Date)
          .subtract(1, 'day')
          .format(format),
      }));

      let currentDayView = startView.clone();

      while (currentDayView.isSameOrBefore(endView, 'day')) {
        let count = 0;
        for (let i = 0; i < ranges.length; i++) {
          if (
            !currentDayView.isBefore(ranges[i].start, 'day') &&
            !currentDayView.isAfter(ranges[i].end, 'day')
          ) {
            count++;
          }
        }

        if (count > 0) {
          eventsData.push({
            allDay: true,
            start: currentDayView.startOf('day').toDate(),
            backgroundColor: theme.colors.green.additional,
            title: getPlural('event', count, { rightCount: true }),
          });
        }

        currentDayView = currentDayView.add(1, 'day');
      }
    }

    return eventsData;
  }, [
    events,
    visible,
    isMonthView,
    showSummary,
    theme.colors.green.additional,
  ]);

  const allDaySlot = useMemo(() => {
    if (!visible || !eventsWrap || !Array.isArray(eventsWrap) || isMonthView) {
      return false;
    }
    const start = dayjs(visible.start);
    const end = dayjs(visible.end);
    if (!eventsWrap.length) {
      return false;
    }
    const visibleEvents = eventsWrap.filter((event) => {
      if (!event.allDay) {
        return false;
      }
      const day = dayjs(event.start as Date);
      return (day.isBefore(end) && day.isAfter(start)) || day.isSame(start);
    });
    return visibleEvents.length > 0;
  }, [eventsWrap, visible, isMonthView]);

  const onEventsSet = useCallback(() => {
    if (isEventsLoaded || !Array.isArray(events) || events.length === 0) {
      return;
    }
    const eventsToSearch = events.filter((event) => !event.allDay);
    if (eventsToSearch.length > 0) {
      eventsToSearch.sort((a, b) => {
        const diff = (a.start as Date).getTime() - (b.start as Date).getTime();
        return Math.sign(diff);
      });
      const dateToScroll = eventsToSearch[0].start as Date;
      ref.current?.getApi()?.scrollToTime(dayjs(dateToScroll).format('HH:mm'));
      setEventsLoaded(true);
    }
  }, [isEventsLoaded, events, ref.current]);

  useEffect(() => {
    if (ref.current) {
      onEventsSet();
    }
  }, [ref.current]);

  const handleEventClick = (event: EventClickArg) => {
    if (event.event.allDay && !enableAllDayClick) {
      return;
    }
    // Added this to fix the bug when modal is opened with event and popover is still open. Currently there is no api to close it.
    const popover = document.querySelector('.fc-popover-close');
    if (popover) {
      // @ts-ignore
      popover?.click();
    }
    eventClick?.(event);
  };

  return (
    <Styles.Container>
      <div ref={headerRef}>
        <Header
          header={header}
          calendar={ref.current}
          headerRightSlot={headerRightSlot}
        />
      </div>

      <FullCalendar
        ref={ref}
        firstDay={0}
        nowIndicator
        dayMaxEvents
        displayEventTime
        eventMaxStack={2}
        handleWindowResize
        events={eventsWrap}
        dayMaxEventRows={2}
        eventOverlap={false}
        height={calendarHeight}
        allDaySlot={allDaySlot}
        windowResize={onEventsSet}
        initialView="timeGridWeek"
        eventContent={eventContent}
        allDayContent={allDayContent}
        dayHeaderContent={dayHeaderContent}
        titleFormat={{ month: 'long', year: 'numeric' }}
        headerToolbar={{ left: '', right: '', center: '' }}
        eventClick={eventClick ? handleEventClick : undefined}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short',
        }}
        datesSet={(dates) => {
          setVisibleDates(dates);
          setEventsLoaded(false);
          datesSet?.(dates);
        }}
        slotLabelFormat={{
          hour: 'numeric',
          meridiem: 'short',
          minute: '2-digit',
          omitZeroMinute: false,
        }}
        eventsSet={(items) => {
          setEventsLoaded(false);
          eventsSet?.(items);
          if (!removeEventScroll) {
            onEventsSet();
          }
        }}
        moreLinkContent={
          mobile ? (
            <Styles.DotButton
              text="•"
              padding="0"
              transparent
              colorType={ButtonColors.LightBlue}
            />
          ) : undefined
        }
        {...props}
      />
    </Styles.Container>
  );
};

export type {
  EventInput,
  FullCalendar,
  EventSourceInput,
  EventCalendarPage,
  EventCalendarProps,
};
